import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, doc, setDoc, serverTimestamp, updateDoc, arrayUnion, runTransaction, where, addDoc } from 'firebase/firestore';
import { notifyAdminsOfProvisionalLicense } from '../utils/licenseNotifications';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import { PlusIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';

const DiscoverCommunities = () => {
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [joinStatus, setJoinStatus] = useState({});

  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // This effect should only run once the authentication process is complete.
    if (authLoading) {
      return; // Wait for authentication to resolve.
    }

    const fetchOrganizations = async () => {
      setOrgsLoading(true);
      try {
        const orgsRef = collection(db, 'organizations');
        const querySnapshot = await getDocs(orgsRef);
        
        const allOrgs = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter(org => org.archived !== true); // Filter out archived orgs
        
        setOrganizations(allOrgs);
      } catch (error) {
        console.error("Failed to fetch organizations:", error);
      } finally {
        setOrgsLoading(false);
      }
    };

    fetchOrganizations();
  }, [authLoading]); // This dependency is key: it triggers the fetch after auth is done.

  const handleAutoJoin = async (org) => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      const memberPayload = {
        name: currentUser?.name || 'Unknown User',
        email: currentUser.email,
        photoURL: currentUser?.photoURL || '',
        status: 'active',
        role: org.settings?.defaultRole || 'Member',
        joinedAt: serverTimestamp()
      };
      const orgMemberRef = doc(db, 'organizations', org.id, 'members', currentUser.uid);
      await setDoc(orgMemberRef, memberPayload);

      const userOrgRef = doc(db, 'users', currentUser.uid, 'organizations', org.id);
      await setDoc(userOrgRef, {
        role: org.settings?.defaultRole || 'Member',
        status: 'active',
        joinedAt: serverTimestamp()
      }, { merge: true });

      await updateDoc(userRef, {
        activeOrgs: arrayUnion(org.id)
      });

      // Auto-allocate license (full or provisional)
      try {
        const orgRef = doc(db, 'organizations', org.id);
        const memberRef = doc(db, 'organizations', org.id, 'members', currentUser.uid);
        const allocationRef = doc(db, 'organizations', org.id, 'licenseAllocations', currentUser.uid);
        const allocationHistoryRef = collection(db, 'organizations', org.id, 'licenseAllocationHistory');

        // Provisional count must be read outside transaction (Firestore web SDK: transaction.get only accepts DocumentReference)
        let provisionalCount = 0;
        try {
          const provisionalSnap = await getDocs(
            query(
              collection(db, 'organizations', org.id, 'licenseAllocations'),
              where('status', '==', 'provisional')
            )
          );
          provisionalCount = provisionalSnap.size;
        } catch (e) {
          console.warn('Could not count provisional licenses:', e);
        }

        const licenseResult = await runTransaction(db, async (transaction) => {
          const orgSnap = await transaction.get(orgRef);
          const allocationSnap = await transaction.get(allocationRef);

          if (!orgSnap.exists()) return null;
          if (allocationSnap.exists()) return null;

          const orgData = orgSnap.data();
          const currentLicense = orgData?.license || {};
          let currentTotal = currentLicense.disciplemakers ?? currentLicense.seats ?? 0;
          const currentUsed = currentLicense.disciplemakersUsed ?? currentLicense.seatsUsed ?? 0;

          // Default to 5 seats (first 5 included) when org has active subscription but no disciplemakers set
          const hasActiveLicense = currentLicense.status === 'active' ||
            currentLicense.status === 'trialing' ||
            currentLicense.platformSubscriptionId ||
            currentLicense.stripeSubscriptionId ||
            currentLicense.earlyAdopter === true;
          if (currentTotal === 0 && hasActiveLicense) {
            currentTotal = 5;
          }

          if (currentTotal === 0) return null;

          const seatsAvailable = currentTotal - currentUsed;
          let licenseStatus = 'allocated';
          let expiresAt = null;

          if (seatsAvailable <= 0) {
            const maxProvisional = currentLicense.maxProvisional ?? 3;
            if (provisionalCount >= maxProvisional) return null;
            licenseStatus = 'provisional';
            expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
          }

          transaction.set(allocationRef, {
            userId: currentUser.uid,
            status: licenseStatus,
            allocatedAt: serverTimestamp(),
            allocatedBy: currentUser.uid,
            reason: 'auto_join',
            organizationId: org.id,
            expiresAt,
            revokedAt: null,
            revokedBy: null,
          });

          const historyDocRef = doc(allocationHistoryRef);
          transaction.set(historyDocRef, {
            userId: currentUser.uid,
            action: 'allocated',
            status: licenseStatus,
            allocatedBy: currentUser.uid,
            reason: 'auto_join',
            organizationId: org.id,
            timestamp: serverTimestamp(),
          });

          transaction.update(memberRef, {
            licenseAllocated: true,
            licenseStatus,
            licenseAllocatedAt: serverTimestamp(),
            licenseExpiresAt: expiresAt,
          });

          if (licenseStatus === 'allocated') {
            const newUsed = currentUsed + 1;
            const newAvailable = currentTotal - newUsed;
            const updateData = {
              'license.disciplemakersUsed': newUsed,
              'license.disciplemakersAvailable': newAvailable,
              'license.seatsUsed': newUsed,
              'license.seatsAvailable': newAvailable,
              'license.updatedAt': serverTimestamp(),
            };
            // Persist effective total when using first-5 fallback so org document stays consistent
            if ((currentLicense.disciplemakers ?? currentLicense.seats ?? 0) === 0) {
              updateData['license.disciplemakers'] = 5;
              updateData['license.seats'] = 5;
            }
            transaction.update(orgRef, updateData);
          }

          return { licenseStatus };
        });

        if (licenseResult?.licenseStatus === 'provisional') {
          notifyAdminsOfProvisionalLicense({
            orgId: org.id,
            orgName: org.displayName || org.name,
            userName: currentUser?.name || currentUser?.email || 'A new member',
            userId: currentUser.uid,
          });
        }
      } catch (licenseError) {
        console.warn('Could not allocate license on auto-join:', licenseError);
      }
      
      setJoinStatus(prev => ({ ...prev, [org.id]: 'joined' }));
    } catch (error) {
      console.error('Error auto-joining organization:', error);
      throw error;
    }
  };

  const handleRequestJoin = async (org) => {
    try {
       const requestRef = doc(db, 'organizations', org.id, 'membershipRequests', currentUser.uid);
        await setDoc(requestRef, {
          userId: currentUser.uid,
          email: currentUser.email,
          name: currentUser?.name || 'Unknown User',
          requestedAt: serverTimestamp(),
          status: 'pending',
        });

        const userOrgRef = doc(db, 'users', currentUser.uid, 'organizations', org.id);
        await setDoc(userOrgRef, {
          role: null,
          status: 'pending',
          requestedAt: serverTimestamp()
        });

        setJoinStatus(prev => ({ ...prev, [org.id]: 'requested' }));
    } catch (error)      {
      console.error('Error requesting to join organization:', error);
      throw error;
    }
  };

  const handleJoin = async (org) => {
    if (!currentUser) {
      alert("You must be logged in to join an organization.");
      return;
    }

    try {
      setIsJoining(true);
      setJoinStatus(prev => ({ ...prev, [org.id]: 'joining' }));

      const requireApproval = org.settings?.requireAdminApproval ?? 
                              (org.settings?.joinProcess === 'manual') ?? true;
      if (!requireApproval) {
        await handleAutoJoin(org);
      } else {
        await handleRequestJoin(org);
      }
    } catch (error) {
      alert('Failed to process join request.');
      setJoinStatus(prev => ({ ...prev, [org.id]: 'error' }));
    } finally {
      setIsJoining(false);
    }
  };

  // Display a loading spinner while either authentication or organization data is loading.
  if (authLoading || orgsLoading) {
    return <div className="flex justify-center items-center h-screen"><LoadingSpinner /></div>;
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900">Discover Communities</h1>
          <p className="mt-4 text-lg text-gray-600">
            Find and request to join public communities, or start your own.
          </p>
        </div>

        <div className="flex justify-end mb-8">
            <button 
                onClick={() => navigate('/create-organization')}
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700">
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Create a Community
            </button>
        </div>

        <div className="space-y-4">
          {organizations.length > 0 ? (
            organizations.map(org => {
              // Because we wait for authLoading, currentUser is guaranteed to be available here.
              const isMember = currentUser?.organizations?.some(userOrg => userOrg.id === org.id && userOrg.status === 'active');
              const isPending = currentUser?.organizations?.some(userOrg => userOrg.id === org.id && userOrg.status === 'pending');
              const isPrivate = org.settings?.visibility === 'private';

              return (
                <div key={org.id} className="bg-white p-6 rounded-lg shadow-sm border flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-grow">
                     <img src={org.logoUrl || '/default-logo.png'} alt={`${org.displayName || org.name} logo`} className="h-16 w-16 object-contain rounded-lg flex-shrink-0" />
                     <div className="flex-1">
                        <h3 className="font-semibold text-xl text-gray-900">{org.displayName || org.name}</h3>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{org.bio}</p>
                     </div>
                  </div>
                  <div className="flex-shrink-0 w-full md:w-auto pt-4 md:pt-0">
                    {isMember ? (
                      <button 
                        onClick={() => navigate(`/organization/${org.id}`)}
                        className="w-full md:w-auto px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md"
                      >
                        View Community
                      </button>
                    ) : isPending || joinStatus[org.id] === 'requested' ? (
                      <span className="w-full md:w-auto flex items-center justify-center px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-100 rounded-md">Requested</span>
                    ) : isPrivate ? (
                       <span className="w-full md:w-auto flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md">
                        Private
                      </span>
                    ) : joinStatus[org.id] === 'joining' ? (
                      <button disabled className="w-full md:w-auto px-4 py-2 text-sm text-white bg-indigo-400 rounded-md cursor-not-allowed">Joining...</button>
                    ) : joinStatus[org.id] === 'joined' ? (
                      <button 
                        onClick={() => navigate(`/organization/${org.id}`)}
                        className="w-full md:w-auto px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md"
                      >
                        Joined! View
                      </button>
                    ) : (
                      <button
                        onClick={() => handleJoin(org)}
                        disabled={isJoining}
                        className="w-full md:w-auto px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                      >
                        {!(org.settings?.requireAdminApproval ?? (org.settings?.joinProcess === 'manual') ?? true) ? 'Join Now' : 'Request to Join'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-16 bg-white rounded-lg shadow-sm border">
              <h3 className="text-xl font-semibold text-gray-700">No communities found</h3>
              <p className="text-gray-500 mt-2">You can create a new community for your group.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscoverCommunities;